<template>
  <div>
    <!-- Profile view (when a student is selected) -->
    <template v-if="selectedMember">
      <div class="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <button
          class="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          @click="selectedMember = null"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-sm" />
        </button>
        <h3 class="font-semibold text-gray-900 text-sm">Profile</h3>
      </div>

      <div class="p-5">
        <!-- Avatar + name -->
        <div class="flex flex-col items-center text-center mb-5">
          <div
            class="w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold uppercase mb-3"
            :class="profileAvatarClass"
          >
            {{ initials(selectedMember.name) }}
          </div>
          <h4 class="text-base font-semibold text-gray-900">{{ selectedMember.name }}</h4>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ selectedMember.role === 'TEACHER' ? 'Teacher' : 'Student' }}
          </p>
        </div>

        <!-- Status badge -->
        <div class="flex justify-center mb-5">
          <span
            v-if="selectedMember.inviteStatus === 'accepted'"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"
          >
            <font-awesome-icon :icon="['fas', 'circle-check']" class="text-[10px]" />
            Joined
          </span>
          <span
            v-else-if="selectedMember.inviteStatus === 'pending'"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"
          >
            <font-awesome-icon :icon="['fas', 'clock']" class="text-[10px]" />
            Invite pending
          </span>
          <span
            v-else-if="selectedMember.inviteStatus === 'expired'"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700"
          >
            <font-awesome-icon :icon="['fas', 'clock']" class="text-[10px]" />
            Invite expired
          </span>
          <span
            v-else-if="selectedMember.role === 'STUDENT'"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600"
          >
            Not invited
          </span>
        </div>

        <!-- Contact details -->
        <div class="space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Email</label>
            <div v-if="selectedMember.email && !editingEmail" class="flex items-center gap-2">
              <span class="text-sm text-gray-900 truncate flex-1">{{ selectedMember.email }}</span>
            </div>
            <div v-else-if="selectedMember.role === 'STUDENT'" class="space-y-2">
              <div class="flex gap-2">
                <input
                  v-model="emailInput"
                  type="email"
                  :placeholder="selectedMember.email ? selectedMember.email : 'Add email address...'"
                  class="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-bc-indigo-400 focus:border-transparent"
                  @keydown.enter="saveEmail"
                />
                <AppButton
                  v-if="emailInput.trim()"
                  variant="primary"
                  size="sm"
                  :loading="savingEmail"
                  @click="saveEmail"
                >
                  Save
                </AppButton>
              </div>
              <p v-if="emailError" class="text-xs text-red-500">{{ emailError }}</p>
            </div>
            <p v-else class="text-sm text-gray-400 italic">No email</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-5 space-y-2">
          <!-- Invite button (if has email, not yet invited or expired) -->
          <AppButton
            v-if="canInvite"
            variant="primary"
            size="md"
            full-width
            :loading="invitingStudentId === selectedMember.studentId"
            @click="$emit('invite', selectedMember)"
          >
            <font-awesome-icon :icon="['fas', 'paper-plane']" class="text-xs" />
            {{ selectedMember.inviteStatus === 'expired' ? 'Re-send invite' : 'Send invite' }}
          </AppButton>

          <!-- View in classes link -->
          <RouterLink
            v-if="selectedMember.studentId"
            :to="`/students/${selectedMember.studentId}`"
            class="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
          >
            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-xs text-gray-400" />
            View full profile
          </RouterLink>
        </div>
      </div>
    </template>

    <!-- Members list view -->
    <template v-else>
      <!-- Header -->
      <div class="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
        <font-awesome-icon :icon="['fas', 'user-group']" class="text-indigo-500 text-sm" />
        <h3 class="font-semibold text-gray-900 text-sm">Members</h3>
        <span
          v-if="members.length"
          class="ml-auto text-xs font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5"
        >
          {{ members.length }}
        </span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="p-4 space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
          <div class="h-4 bg-gray-200 rounded animate-pulse flex-1" />
        </div>
      </div>

      <template v-else>
        <!-- Invite All button -->
        <div v-if="uninvitedWithEmailCount > 0" class="px-5 pt-4 pb-2">
          <AppButton
            variant="primary"
            size="md"
            full-width
            :loading="invitingAll"
            @click="$emit('invite-all')"
          >
            <font-awesome-icon :icon="['fas', 'paper-plane']" class="text-xs" />
            Invite all ({{ uninvitedWithEmailCount }})
          </AppButton>
        </div>

        <!-- Members list -->
        <div class="max-h-[500px] overflow-y-auto">
          <button
            v-for="member in sortedMembers"
            :key="member.studentId || member.userId || member.name"
            class="w-full flex items-center gap-3 px-5 py-2.5 transition-colors text-left hover:bg-gray-50"
            :class="member.role === 'STUDENT' ? 'cursor-pointer' : 'cursor-default'"
            @click="member.role === 'STUDENT' ? (selectedMember = member) : undefined"
          >
            <!-- Avatar -->
            <div class="relative shrink-0">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold uppercase"
                :class="avatarClass(member)"
              >
                {{ initials(member.name) }}
              </div>
              <div
                v-if="member.inviteStatus === 'accepted'"
                class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-white"
              >
                <font-awesome-icon :icon="['fas', 'check']" class="text-white text-[7px]" />
              </div>
            </div>

            <!-- Name + status -->
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 truncate">{{ member.name }}</p>
              <p v-if="member.role === 'TEACHER'" class="text-xs text-indigo-500 font-medium">Teacher</p>
              <p v-else-if="member.inviteStatus === 'accepted'" class="text-xs text-green-600">Joined</p>
              <p v-else-if="member.inviteStatus === 'pending'" class="text-xs text-amber-600">Invite pending</p>
              <p v-else-if="member.inviteStatus === 'expired'" class="text-xs text-red-500">Invite expired</p>
              <p v-else-if="member.email" class="text-xs text-gray-400">Not invited</p>
              <p v-else class="text-xs text-gray-400">No email</p>
            </div>

            <!-- Chevron for students -->
            <font-awesome-icon
              v-if="member.role === 'STUDENT'"
              :icon="['fas', 'chevron-right']"
              class="text-[10px] text-gray-300 shrink-0"
            />
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { apiClient } from '@/api'
import type { SpaceMember } from '@/api'

const props = defineProps<{
  members: SpaceMember[]
  loading?: boolean
  invitingStudentId?: string | null
  invitingAll?: boolean
}>()

defineEmits<{
  invite: [member: SpaceMember]
  'invite-all': []
}>()

const selectedMember = ref<SpaceMember | null>(null)
const editingEmail = ref(false)
const emailInput = ref('')
const savingEmail = ref(false)
const emailError = ref<string | null>(null)

const uninvitedWithEmailCount = computed(() =>
  props.members.filter(m => m.role === 'STUDENT' && !m.inviteStatus && m.email).length,
)

const canInvite = computed(() => {
  if (!selectedMember.value) return false
  if (selectedMember.value.role !== 'STUDENT') return false
  if (!selectedMember.value.email && !emailInput.value.trim()) return false
  if (selectedMember.value.inviteStatus === 'accepted') return false
  if (selectedMember.value.inviteStatus === 'pending') return false
  return true
})

const profileAvatarClass = computed(() => {
  const m = selectedMember.value
  if (!m) return 'bg-gray-100 text-gray-500'
  if (m.role === 'TEACHER') return 'bg-indigo-100 text-indigo-700'
  if (m.inviteStatus === 'accepted') return 'bg-green-100 text-green-700'
  if (m.inviteStatus === 'pending') return 'bg-amber-50 text-amber-600'
  return 'bg-gray-100 text-gray-500'
})

const sortedMembers = computed(() => {
  const roleOrder: Record<string, number> = { TEACHER: 0, STUDENT: 1, GUARDIAN: 2 }
  const statusOrder: Record<string, number> = { accepted: 0, pending: 1, expired: 2 }

  return [...props.members].sort((a, b) => {
    const roleDiff = (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9)
    if (roleDiff !== 0) return roleDiff

    if (a.role === 'STUDENT' && b.role === 'STUDENT') {
      const aStatus = a.inviteStatus ? (statusOrder[a.inviteStatus] ?? 3) : 3
      const bStatus = b.inviteStatus ? (statusOrder[b.inviteStatus] ?? 3) : 3
      if (aStatus !== bStatus) return aStatus - bStatus
    }

    return a.name.localeCompare(b.name)
  })
})

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

function avatarClass(member: SpaceMember): string {
  if (member.role === 'TEACHER') return 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300'
  if (member.inviteStatus === 'accepted') return 'bg-green-100 text-green-700 ring-2 ring-green-400'
  if (member.inviteStatus === 'pending') return 'bg-amber-50 text-amber-600'
  return 'bg-gray-100 text-gray-500'
}

async function saveEmail() {
  const email = emailInput.value.trim()
  if (!email || !selectedMember.value?.studentId) return

  savingEmail.value = true
  emailError.value = null
  try {
    await apiClient.patch(`/students/${selectedMember.value.studentId}`, { email })
    // Update the member's email locally
    selectedMember.value = { ...selectedMember.value, email }
    emailInput.value = ''
  } catch (err) {
    console.error('Failed to update email:', err)
    emailError.value = 'Failed to save email'
  } finally {
    savingEmail.value = false
  }
}
</script>
